/**
 * Authentication API
 *
 * Stateless API functions for authentication endpoints.
 */

import { apiRequestForm } from "./client";
import { authUrl, csrfUrl } from "../components/constants";
import { getCookie } from "../utils/cookie";
import type {
  EmailSubmitResponse,
  OTPVerifyResponse,
  GoogleAuthResponse,
} from "./types";

// --- Email Authentication ---

/**
 * Request verification code to be sent to email
 */
export async function sendVerificationCode(
  email: string,
  csrfToken: string,
): Promise<{ response: Response; data: EmailSubmitResponse }> {
  const { response, data } = await apiRequestForm(
    authUrl.get_email,
    { email },
    {
      headers: { "X-CSRFToken": csrfToken },
      mode: "cors",
    },
  );

  return { response, data: data as EmailSubmitResponse };
}

/**
 * Verify OTP code and complete authentication
 */
export async function verifyOTPCode(
  email: string,
  code: string,
  csrfToken: string,
): Promise<{ response: Response; data: OTPVerifyResponse }> {
  const { response, data } = await apiRequestForm(
    authUrl.get_pass,
    { email, code },
    {
      headers: { "X-CSRFToken": csrfToken },
      mode: "cors",
    },
  );

  return { response, data: data as OTPVerifyResponse };
}

// --- Google OAuth ---

/**
 * Authenticate with Google OAuth credential
 */
export async function authenticateWithGoogle(
  credential: string,
): Promise<{ response: Response; data: GoogleAuthResponse }> {
  const { response, data } = await apiRequestForm(
    authUrl.google,
    { token: credential },
    { mode: "cors" },
  );

  return { response, data: data as GoogleAuthResponse };
}

// --- CSRF ---

/**
 * Ensure CSRF cookie is set (useful after third-party auth flows)
 */
export async function ensureCsrfCookie(): Promise<string | null> {
  // Split-domain deploy: the backend's own csrftoken cookie isn't readable by JS on the
  // frontend domain, so we MUST copy the token from the response BODY into a same-domain
  // cookie that getCookie("csrftoken") can read. (The old version only hit the endpoint and
  // never set a readable cookie — so a brand-new visitor whose app-load fetch hiccuped had
  // no token at all, and login failed with "CSRF token not found".)
  try {
    const res = await fetch(csrfUrl, { credentials: "include" });
    const data = await res.json();
    if (data && data.csrfToken) {
      document.cookie = `csrftoken=${data.csrfToken}; path=/; SameSite=Lax`;
      return data.csrfToken;
    }
  } catch (e) {
    /* fall through and return whatever cookie we already have */
  }
  return getCookie("csrftoken");
}

// Re-export validation utilities for backward compatibility
export { EMAIL_REGEX, isValidEmail } from "../utils/validation";
